import { ReactNode, useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { toast } from 'react-toastify'

import {
  Autocomplete,
  Button,
  Dialog,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Select,
  Switch,
  Textarea,
  Textfield
} from '@clientbase/clientbase-library'
import { yupResolver } from '@hookform/resolvers/yup'
import {
  FormControlLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Stack,
  Typography
} from '@mui/material'
import { Box, useMediaQuery } from '@mui/system'
import { DataService } from 'api/DataService'
import { useFetch } from 'hooks/use-fetch'
import { useSelectRowTable } from 'hooks/use-select-row-table'
import { useInsertAtCursor } from 'hooks/useInsertAtCursor'
import { NFeParams } from 'models/NFe'
import { Bank } from 'models/Overview/Transfer'
import {
  constructorAutoCompleteProvider,
  constructorAutoCompleteServices
} from 'models/ServiceProvider'
import DialogBlockNewInvoice from 'templates/Invoice/components/Dialog/DialogBlockNewInvoice'

import AutocompleteCustomers from 'components/AutocompleteCustomers'
import IconContainer from 'components/IconContainer/IconContainer'
import PopoverNewVariant from 'components/PopoverNewVariant'
import {
  hasNfseDueDateTokens,
  NFSE_DUE_DATE_VARIANTS,
  NFSE_ISSUE_VARIANTS,
  validateNfseDescriptionVariants
} from 'components/PopoverNewVariant/variants'

import { currency } from 'utils'
import { toastProps } from 'utils/types/toast-props'
import { toastFirstError } from 'utils/yupResolverWithToast'

import { theme } from 'styles/theme'

import ChargeTable from './components/Table/Table'
import { Record as CustomerChargeRecord } from './components/Table/Table'
import {
  ModalInvoiceFieldsSchema,
  SchemaModalInvoice
} from './ModalInvoice.Schema'
import { enumNature, FilterOptions, NatureOption } from './ModalInvoice.utils'
import * as S from './styles'

interface InvoiceSettingsContentProps {
  customer?: {
    uuid: string
    label: string
  }
  isEdit?: boolean
  invoice?: NFeParams
  // Quando true, o submit EMITE uma nota nova (POST /v1/nfses/issue) em vez de
  // reemitir a nota alvo (PATCH .../reissue). Usado no fluxo de editar cobrança
  // emitida: a nota antiga é confirmada (não 'failed'), então o caminho correto
  // é emitir uma nova vinculada à cobrança e cancelar a antiga após o sucesso.
  // Mantém isEdit=true para preservar pré-preenchimento, pré-seleção e títulos.
  emitNewOnSubmit?: boolean
  handleToggle: () => void
  handleReloadTable?: () => void
  openInvoiceSettings?: () => void
  // Pré-preenchimento em modo criação (atalho "Emitir Nota Fiscal" da cobrança):
  initialAmount?: string
  preselectedCharge?: CustomerChargeRecord
  // SC-10999: seletor "Empresa" da criação na visão multi-empresas
  companySelector?: ReactNode
}

const defaultValues = {
  nature_operation: '1',
  iss_retention: 'no',
  nfse_issuer_uuid: ''
}

const InvoiceSettingsContent = ({
  isEdit = false,
  invoice,
  emitNewOnSubmit = false,
  handleToggle,
  handleReloadTable,
  openInvoiceSettings,
  customer,
  initialAmount,
  preselectedCharge,
  companySelector
}: InvoiceSettingsContentProps) => {
  // Determine initial filter based on invoice billings status
  const getInitialFilter = () => {
    if (isEdit && invoice?.billings && invoice.billings.length > 0) {
      // Use the status of the first billing as the initial filter
      const firstBillingStatus = invoice.billings[0].status
      return firstBillingStatus === 'paid'
        ? 'paid'
        : firstBillingStatus === 'pending'
        ? 'pending'
        : 'open_payment'
    }
    // Atalho a partir de um recebimento: a cobrança fixada já está paga.
    if (preselectedCharge?.status === 'paid') return 'paid'
    return 'open_payment'
  }

  const [filter, setFilter] = useState(getInitialFilter())
  const amount = invoice?.nfse_item?.amount || '0'
  const [isOpenNoIssuersDialog, setIsOpenNoIssuersDialog] = useState(false)
  const match = useMediaQuery('(max-width: 958px)', { noSsr: false })

  const [modals, setModals] = useState<{
    open: boolean
    dataForm: ModalInvoiceFieldsSchema | null
    loading: boolean
  }>({ open: false, dataForm: null, loading: false })

  const [loadedCharges, setLoadedCharges] = useState<CustomerChargeRecord[]>([])

  const { control, watch, setValue, setError, handleSubmit } =
    useForm<ModalInvoiceFieldsSchema>({
      defaultValues: !invoice
        ? {
            ...defaultValues,
            amount: initialAmount
              ? parseFloat(initialAmount).toFixed(2)
              : undefined,
            link_billing: !!preselectedCharge,
            customer_uuid: {
              label: customer?.label,
              value: customer?.uuid
            }
          }
        : {
            iss_retention: invoice?.nfse_item?.iss_retention ? 'yes' : 'no',
            service_list_code: {
              name: invoice?.nfse_item?.nfse_issuer_service?.description,
              value: invoice?.nfse_item?.nfse_issuer_service?.uuid
            },
            amount: parseFloat(amount).toFixed(2),
            description: invoice?.nfse_item?.description,
            nfse_issuer_uuid: invoice?.nfse_issuer_uuid,
            customer_uuid: {
              label: invoice?.customer?.name,
              value: invoice?.customer?.uuid
            },
            nature_operation:
              enumNature[
                invoice?.nfse_item?.nature_operation as keyof typeof enumNature
              ],
            link_billing: !!invoice?.billings && invoice.billings.length > 0
          },
      resolver: yupResolver(SchemaModalInvoice)
    })

  const { trackCursor, insertAtCursor } = useInsertAtCursor()

  const { select, setSelect } = useSelectRowTable({
    useSelected: true
  })

  const [, resultNfse] = useFetch({
    method: 'GET',
    shouldRun: true,
    url: '/v1/nfse_issuers/autocomplete'
  })

  const [fetchServices, resultServices] = useFetch({
    method: 'GET',
    shouldRun: false,
    url: `/v1/nfse_issuers`
  })

  // As variáveis de vencimento só fazem sentido quando há UM vencimento definido:
  // cobranças vinculadas, todas vencendo no mesmo mês. O backend rejeita o resto.
  const selectedDueMonths = loadedCharges
    .filter((charge) => select.selected.includes(charge.uuid))
    .map((charge) => charge.due_date?.slice(0, 7))
    .filter(Boolean)

  const canUseDueDateVariants =
    !!watch('link_billing') &&
    selectedDueMonths.length > 0 &&
    new Set(selectedDueMonths).size === 1

  const handleOpen = (dataForm: ModalInvoiceFieldsSchema) => {
    setModals(() => ({ dataForm: dataForm, open: true, loading: false }))
  }

  const handleCancel = () => {
    setModals(() => ({ dataForm: null, open: false, loading: false }))
  }

  const handleCloseBlockNewInvoice = () => {
    setIsOpenNoIssuersDialog(false)
    handleToggle()
    !!openInvoiceSettings && openInvoiceSettings()
  }

  const onSubmit = async (
    dataForm: ModalInvoiceFieldsSchema
  ): Promise<void> => {
    // Guardas ANTES do toast.loading: senão o spinner fica preso ao abortar.
    const formatError = validateNfseDescriptionVariants(dataForm.description)
    if (formatError) {
      setError('description', { message: formatError, type: 'pattern' })
      return
    }

    if (hasNfseDueDateTokens(dataForm.description) && !canUseDueDateVariants) {
      setError('description', {
        type: 'pattern',
        message:
          selectedDueMonths.length > 1
            ? 'A variável {{mes_vencimento}} não pode ser usada: as cobranças vinculadas têm vencimentos em meses diferentes.'
            : 'A variável {{mes_vencimento}} só pode ser usada em notas vinculadas a uma cobrança com vencimento definido.'
      })
      return
    }

    const id = toast.loading(
      isEdit ? 'Reemitindo nota fiscal...' : 'Emitindo nota fiscal...'
    )
    const {
      customer_uuid,
      nfse_issuer_uuid,
      description,
      amount,
      iss_retention,
      nature_operation,
      service_list_code
    } = dataForm

    const value = currency(amount.toString())
      .replace(/\./g, '')
      .replace(',', '.')

    const data = {
      customer_uuid: customer_uuid.value,
      nfse_issuer_uuid,
      description,
      amount: Number(value),
      iss_retention: iss_retention === 'yes' ? true : false,
      nfse_issuer_service_uuid: service_list_code.value,
      nature_operation: Number(nature_operation),
      uuids_billing: ''
    }

    if (select.selected.length > 0 && dataForm?.link_billing) {
      const uuids = select.selected
      let uuids_billing = ''
      uuids.map((value) => (uuids_billing += `${value};`))

      data.uuids_billing = uuids_billing
    }

    // emitNewOnSubmit força a emissão de uma nota nova mesmo com isEdit=true
    // (cobrança emitida cuja nota confirmada será cancelada após a emissão).
    const useEmitEndpoint = emitNewOnSubmit || !isEdit
    const response = await DataService({
      type: useEmitEndpoint ? 'POST' : 'PATCH',
      data,
      url: useEmitEndpoint
        ? '/v1/nfses/issue'
        : `/v1/nfses/${invoice?.uuid}/reissue`
    })

    const errorMessage =
      response.error && (response.detail_error || response.message)

    toast.update(id, {
      render: response.error ? errorMessage : response.data.message,
      type: response.error ? 'error' : 'success',
      ...toastProps
    })

    if (response.status === 200) {
      handleToggle()
      !!handleReloadTable && handleReloadTable()
    }

    handleCancel()
  }

  useEffect(() => {
    if (
      typeof watch('nfse_issuer_uuid') === 'string' &&
      watch('nfse_issuer_uuid') !== ''
    ) {
      fetchServices({ paramUrl: `${watch('nfse_issuer_uuid')}/services` })
    }
  }, [watch('nfse_issuer_uuid')])

  // Auto-select service item when only one is available
  useEffect(() => {
    if (resultServices?.data) {
      const services = constructorAutoCompleteServices(resultServices.data)
      if (services.length === 1 && !watch('service_list_code')?.value) {
        setValue('service_list_code', {
          name: services[0].description,
          value: services[0].uuid
        })
      }
    }
  }, [resultServices])

  useEffect(() => {
    if (resultNfse?.data) {
      setIsOpenNoIssuersDialog(resultNfse.data.length <= 0)

      // Auto-select if there's only one provider and no value is set
      if (resultNfse.data.length === 1 && !watch('nfse_issuer_uuid')) {
        const providers = constructorAutoCompleteProvider(resultNfse.data)
        if (providers.length === 1) {
          setValue('nfse_issuer_uuid', providers[0].uuid)
        }
      }
    }
  }, [resultNfse])

  // Pre-select linked billings when editing/reissuing invoice
  useEffect(() => {
    if (isEdit && invoice?.billings && invoice.billings.length > 0) {
      const billingUuids = invoice.billings.map((billing) => billing.uuid)
      setSelect((prevState) => ({
        ...prevState,
        selected: billingUuids
      }))
    }
  }, [isEdit, invoice])

  // Pré-seleciona a cobrança no atalho de emissão (modo criação)
  useEffect(() => {
    if (!isEdit && preselectedCharge?.uuid) {
      setSelect((prevState) => ({
        ...prevState,
        selected: [preselectedCharge.uuid]
      }))
    }
  }, [isEdit, preselectedCharge])

  if (isOpenNoIssuersDialog) {
    return (
      <DialogBlockNewInvoice
        open={isOpenNoIssuersDialog}
        closeDialog={handleCloseBlockNewInvoice}
      />
    )
  }

  return (
    <ModalBody>
      <ModalHeader onClose={() => handleToggle()}>
        <Stack direction="row" alignItems="center" gap={2}>
          <IconContainer icon="attachMoney" />
          <Typography variant="mdBold">
            {!isEdit ? 'Nova nota fiscal' : 'Reemitir nota fiscal'}
          </Typography>
        </Stack>
      </ModalHeader>

      <ModalContent>
        {companySelector}
        <S.Wrapper>
          <S.ItemsWrapper sx={{ maxWidth: '100%', width: '100%' }}>
            <S.FieldWrapper sx={{ maxWidth: '100%', width: '100%' }}>
              <S.Legend>Prestador de serviços</S.Legend>
              <S.Heading>Prestador de serviços</S.Heading>

              <S.Content>
                <Controller
                  name="nfse_issuer_uuid"
                  control={control}
                  render={({
                    field: { onChange, value },
                    fieldState: { error }
                  }) => (
                    <Select
                      helperText={error?.message}
                      error={!!error?.message}
                      displayEmpty
                      fullWidth
                      onChange={onChange}
                      value={value}
                      sx={{
                        width: match ? `${window.innerWidth - 90}px` : '100%'
                      }}
                    >
                      <MenuItem disabled value="" color="#C5C5C5">
                        <Typography
                          color="#C5C5C5"
                          fontWeight={300}
                          textOverflow="ellipsis"
                        >
                          Selecione o prestador
                        </Typography>
                      </MenuItem>
                      {resultNfse &&
                        constructorAutoCompleteProvider(resultNfse.data).map(
                          ({ legal_name, uuid }) => (
                            <MenuItem
                              key={uuid}
                              value={uuid}
                              sx={{ width: '100%', whiteSpace: 'break-spaces' }}
                            >
                              {legal_name}
                            </MenuItem>
                          )
                        )}
                    </Select>
                  )}
                />
              </S.Content>
            </S.FieldWrapper>
          </S.ItemsWrapper>

          <S.ItemsWrapper>
            <S.FieldWrapper>
              <S.Legend>Cliente</S.Legend>
              <S.Heading>Cliente</S.Heading>

              <S.Content>
                <S.ItemsWrapper>
                  <Box>
                    <Controller
                      control={control}
                      name="customer_uuid"
                      render={({
                        field: { onChange, value },
                        fieldState: { error }
                      }) => {
                        const errorNotTyped = error as any

                        return (
                          <AutocompleteCustomers
                            disabled={!!customer?.uuid}
                            onChange={(
                              _: React.SyntheticEvent,
                              value: unknown
                            ) => {
                              setValue('link_billing', false)
                              setSelect((prevState) => ({
                                ...prevState,
                                selected: []
                              }))
                              onChange(value ?? { value: '', label: '' })
                            }}
                            value={customer?.uuid ? customer : value}
                            errorMessage={errorNotTyped?.label?.message}
                            placeholder="Digite aqui o cliente"
                          />
                        )
                      }}
                    />
                  </Box>
                </S.ItemsWrapper>
              </S.Content>
            </S.FieldWrapper>

            <S.FieldWrapper>
              <S.Legend>Valor da nota</S.Legend>
              <S.Heading>Valor da nota</S.Heading>

              <S.Content>
                <Controller
                  control={control}
                  name="amount"
                  render={({
                    field: { onChange, value },
                    fieldState: { error }
                  }) => (
                    <Textfield
                      customStartAdornment={
                        <Typography mr="6px" variant="smLight">
                          R$
                        </Typography>
                      }
                      placeholder="Digite aqui o valor da nota"
                      helperText={error?.message}
                      error={!!error?.message}
                      mask="money"
                      onChange={onChange}
                      startAdornment={true}
                      value={value}
                    />
                  )}
                />
              </S.Content>
            </S.FieldWrapper>
          </S.ItemsWrapper>

          <S.ItemsWrapper minWidth={400}>
            <S.FieldWrapper>
              <S.Legend>Natureza da operação</S.Legend>
              <S.Heading>Natureza da operação</S.Heading>
              <S.Content>
                <Controller
                  name="nature_operation"
                  control={control}
                  render={({
                    field: { onChange, value },
                    fieldState: { error }
                  }) => (
                    <Select
                      helperText={error?.message}
                      error={!!error?.message}
                      onChange={onChange}
                      label="Natureza da operação"
                      value={value}
                      displayEmpty
                      fullWidth
                    >
                      <MenuItem
                        disabled
                        value=""
                        color={theme.palette.neutral[600]}
                      >
                        <Typography
                          color={theme.palette.neutral[600]}
                          fontWeight={300}
                        >
                          Selecione uma natureza da operação
                        </Typography>
                      </MenuItem>
                      {NatureOption.map(({ label, value }) => (
                        <MenuItem key={value} value={value}>
                          {label}
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                />
              </S.Content>
            </S.FieldWrapper>
          </S.ItemsWrapper>

          <S.Grid>
            <S.FieldWrapper>
              <S.Legend>Item de serviço</S.Legend>
              <S.Heading>Item de serviço</S.Heading>
              <S.Content>
                <Controller
                  name="service_list_code"
                  control={control}
                  render={({
                    field: { onChange, value },
                    fieldState: { error }
                  }) => {
                    const errorNotTyped = error as any
                    return (
                      <Autocomplete
                        onChange={(_, value) => onChange(value)}
                        value={value}
                        options={
                          resultServices
                            ? constructorAutoCompleteServices(
                                resultServices.data
                              ).map(({ description, uuid, code }) => ({
                                name: `${code} - ${description}`,
                                value: uuid
                              }))
                            : []
                        }
                        noOptionsText="Nenhum serviço encontrado"
                        autoHighlight
                        getOptionLabel={(option) => (option as Bank).name}
                        isOptionEqualToValue={(option, value) =>
                          (option as Bank).uuid === (value as Bank).uuid
                        }
                        renderOption={(props, option) => {
                          const optionTyped = option as {
                            name: string
                            value: string
                          }
                          return (
                            <Box
                              component="li"
                              {...props}
                              key={optionTyped.value}
                            >
                              {optionTyped.name}
                            </Box>
                          )
                        }}
                        disabled={!watch('nfse_issuer_uuid')}
                        renderInput={(params) => (
                          <Textfield
                            helperText={errorNotTyped?.name?.message}
                            error={!!errorNotTyped?.name?.message}
                            placeholder="Digite aqui o item de serviço"
                            {...params}
                          />
                        )}
                      />
                    )
                  }}
                />
              </S.Content>
            </S.FieldWrapper>

            <S.FieldWrapper>
              <S.Legend>ISS retido na fonte</S.Legend>
              <S.Heading>ISS retido na fonte</S.Heading>

              <S.Content>
                <Controller
                  name="iss_retention"
                  control={control}
                  render={({
                    field: { onChange, value },
                    fieldState: { error }
                  }) => (
                    <Select
                      helperText={error?.message}
                      error={!!error?.message}
                      fullWidth
                      onChange={onChange}
                      value={value}
                      displayEmpty
                    >
                      <MenuItem
                        disabled
                        value=""
                        color={theme.palette.neutral[600]}
                      >
                        <Typography
                          color={theme.palette.neutral[600]}
                          fontWeight={300}
                        >
                          Selecione uma opção
                        </Typography>
                      </MenuItem>
                      <MenuItem value={'yes'}>Sim</MenuItem>
                      <MenuItem value={'no'}>Não</MenuItem>
                    </Select>
                  )}
                />
              </S.Content>
            </S.FieldWrapper>
          </S.Grid>

          <S.FieldWrapper>
            <S.Legend>Descrição dos serviços</S.Legend>
            <S.Heading>Descrição dos serviços</S.Heading>
            <S.Content>
              {/* Agrupa textarea + botão: o gap:36 do S.Content vale pro conjunto. */}
              <Box>
                <Controller
                  name="description"
                  control={control}
                  render={({
                    field: { onChange, value },
                    fieldState: { error }
                  }) => {
                    return (
                      <>
                        <Textarea
                          style={{
                            height: '180px',
                            overflow: 'auto',
                            resize: 'vertical',
                            maxHeight: '50vh'
                          }}
                          value={value}
                          onChange={onChange}
                          onSelect={trackCursor}
                          error={!!error?.message}
                          helperText={error?.message}
                          placeholder="Digite aqui a descrição"
                        />
                      </>
                    )
                  }}
                />

                {!isEdit && (
                  <PopoverNewVariant
                    variants={
                      canUseDueDateVariants
                        ? [...NFSE_ISSUE_VARIANTS, ...NFSE_DUE_DATE_VARIANTS]
                        : NFSE_ISSUE_VARIANTS
                    }
                    description={watch('description')}
                    setDescription={(message) =>
                      setValue('description', message)
                    }
                    insertAtCursor={insertAtCursor}
                    noChars
                  />
                )}
              </Box>
            </S.Content>
          </S.FieldWrapper>

          <S.FieldWrapper>
            <S.Legend>Vincular cobranças</S.Legend>
            <S.Heading>
              <Box display="flex" alignItems="center" justifyContent="center">
                Vincular cobranças
                <Controller
                  name="link_billing"
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <Switch
                      value={value}
                      checked={value}
                      onChange={onChange}
                      disabled={!watch('customer_uuid')}
                    />
                  )}
                />
              </Box>
            </S.Heading>
            {watch('link_billing') && (
              <S.Content>
                <Box display="flex" justifyContent="center" gap={4.5}>
                  <RadioGroup
                    value={filter}
                    onChange={(_, value) => {
                      setSelect((prevState) => ({ ...prevState, selected: [] }))
                      setFilter(value)
                    }}
                    row
                  >
                    <Box display="flex" gap={4.5}>
                      {FilterOptions.map(({ label, value }, index) => (
                        <FormControlLabel
                          value={value}
                          control={<Radio />}
                          label={label}
                          key={index}
                          checked={value === filter}
                        />
                      ))}
                    </Box>
                  </RadioGroup>
                </Box>

                <Box>
                  <ChargeTable
                    baseFilters={{
                      customer_uuid: watch('customer_uuid').value,
                      status: filter
                    }}
                    pinnedCharge={preselectedCharge}
                    onRecordsChange={setLoadedCharges}
                    select={{
                      useSelect: select.useSelect,
                      selected: select.selected,
                      setSelected: setSelect
                    }}
                  />
                </Box>
              </S.Content>
            )}
          </S.FieldWrapper>

          <Dialog
            icon="info"
            title={`${isEdit ? 'Reemitir' : 'Emitir'} nota fiscal`}
            description={`Confirmar ${
              isEdit ? 'reemissão' : 'emissão'
            } da nota fiscal?`}
            setIsOpenDialog={handleCancel}
            isOpenDialog={modals.open}
            cancelButton
            // Este modal pode ser aberto por cima do modal de reemissão
            // (zIndex 1500) no fluxo de editar cobrança emitida. Sem subir o
            // zIndex aqui, o Dialog de confirmação abre ATRÁS e o clique em
            // "Reemitir nota" parece não fazer nada.
            style={{ zIndex: 1600 }}
          >
            <Button
              disabled={modals.loading}
              onClick={() =>
                onSubmit(modals.dataForm as ModalInvoiceFieldsSchema)
              }
              fullWidth
            >
              {isEdit ? 'Reemitir' : 'Emitir'}
            </Button>
          </Dialog>
        </S.Wrapper>
      </ModalContent>

      <ModalFooter>
        <Button
          variantButton="primary"
          onClick={handleSubmit(handleOpen, toastFirstError)}
        >
          {isEdit ? 'Reemitir' : 'Emitir'} nota
        </Button>
      </ModalFooter>
    </ModalBody>
  )
}

export default InvoiceSettingsContent
